import React, { useState, useEffect } from 'react';
import { Globe, X, ArrowRight, MapPin } from 'lucide-react'; 

// This component detects if a user is on the "wrong" regional domain and prompts them to switch.
const RegionRedirectModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [targetRegion, setTargetRegion] = useState({ name: '', url: '', flag: '' });

    useEffect(() => {
        const checkRegion = async () => {
            // 1. Check if user has already dismissed this popup
            const hasDismissed = localStorage.getItem('region_preference_dismissed');
            if (hasDismissed) return;

            try {
                // 2. Get current hostname (e.g., crm.procleanerus.com)
                const currentHostname = window.location.hostname;
                
                // 3. Fetch user's location based on IP
                // Note: ipapi.co is free up to 1000 requests/day. 
                const response = await fetch('https://ipapi.co/json/');
                if (!response.ok) return; // Fail silently if API is down/blocked
                
                const data = await response.json();
                const userCountryCode = data.country_code; // "GB", "US", etc.

                // 4. Define Logic
                // We check if the hostname contains the base domain to cover subdomains like 'crm.' or 'theone.'
                const isUKDomain = currentHostname.includes('procleaneruk.co.uk');
                const isUSDomain = currentHostname.includes('procleanerus.com');

                // CASE A: User is in UK, but on US site -> Suggest UK
                if (userCountryCode === 'GB' && isUSDomain) {
                    setTargetRegion({
                        name: 'United Kingdom',
                        url: 'https://theone.procleaneruk.co.uk', // ⭐ UPDATED: Points to UK Subdomain
                        flag: '🇬🇧'
                    });
                    setShowModal(true);
                }
                
                // CASE B: User is in US, but on UK site -> Suggest US
                else if (userCountryCode === 'US' && isUKDomain) {
                    setTargetRegion({
                        name: 'United States',
                        url: 'https://crm.procleanerus.com', // ⭐ UPDATED: Points to US Subdomain
                        flag: '🇺🇸'
                    });
                    setShowModal(true);
                }

            } catch (error) {
                console.warn("Region detection failed:", error);
                // Fail silently, do not annoy user with errors
            }
        };

        // Run check after a short delay to allow page to load first
        const timer = setTimeout(checkRegion, 2000);
        return () => clearTimeout(timer);
    }, []);

    const handleSwitch = () => {
        if (!targetRegion.url) return;
        
        // Preserve the current path (e.g. /login) and query params (e.g. ?ref=123)
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        
        // Redirect to new domain
        window.location.href = `${targetRegion.url}${currentPath}${currentSearch}`;
    };

    const handleDismiss = () => {
        // Save preference so we don't ask again
        localStorage.setItem('region_preference_dismissed', 'true');
        setShowModal(false);
    };

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background Overlay */}
            <div 
                className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" 
                aria-hidden="true"
                onClick={handleDismiss}
            ></div>

            {/* Modal Panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div 
                className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full border border-gray-200 dark:border-gray-700"
            >
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900 sm:mx-0 sm:h-10 sm:w-10">
                            <Globe className="h-6 w-6 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <div className="flex justify-between items-start">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                    Wrong Region?
                                </h3>
                                <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-500">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 dark:text-gray-300">
                                    It looks like you are visiting us from <span className="font-semibold text-gray-700 dark:text-gray-200"><MapPin className="inline h-3 w-3 mb-1"/> {targetRegion.name === 'United Kingdom' ? 'the UK' : 'the US'}</span>.
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-300 mt-2">
                                    Would you like to switch to our {targetRegion.name} website for local pricing and support?
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        onClick={handleSwitch}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm items-center"
                    >
                        Go to {targetRegion.flag} {targetRegion.name} <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                        Stay here
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegionRedirectModal;